import i18n from "../lang/i18n";
import { getApiErrorCode } from "./apiError";

export const isCanceledError = (error) =>
  error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

const collectMessages = (payload) => {
  if (payload == null) return [];

  if (typeof payload === "string") {
    const text = payload.trim();
    return text ? [text] : [];
  }

  if (Array.isArray(payload)) {
    return payload.flatMap((item) => collectMessages(item));
  }

  if (typeof payload === "object") {
    return Object.values(payload).flatMap((value) => collectMessages(value));
  }

  return [];
};

const extractMessages = (error) => {
  const data = error?.response?.data;
  const messages = collectMessages(data);

  if (messages.length > 0) {
    return messages;
  }

  const fallback =
    error?.response?.statusText ||
    error?.message ||
    "";

  return fallback ? [String(fallback)] : [];
};

const extractDetail = (error) => extractMessages(error).join(" ").toLowerCase();

export const resolveExamErrorKey = (
  error,
  fallbackKey = "exam_taking.validation_unexpected_error"
) => {
  if (isCanceledError(error)) {
    return null;
  }

  if (!error?.response) {
    return "exam_taking.validation_network_error";
  }

  const detail = extractDetail(error);

  if (detail.includes("not available yet") || detail.includes("availability window has ended")) {
    return "exam_taking.validation_exam_unavailable";
  }

  if (detail.includes("maximum number of attempts reached") || detail.includes("max attempts")) {
    return "exam_taking.validation_max_attempts_reached";
  }

  if (detail.includes("cooldown active")) {
    return "exam_taking.validation_cooldown_active";
  }

  if (detail.includes("attempt is not in progress")) {
    return "exam_taking.validation_attempt_not_in_progress";
  }

  if (detail.includes("time is up") || detail.includes("auto-submitted")) {
    return "exam_taking.validation_time_up";
  }

  if (detail.includes("selected answer key does not belong")) {
    return "exam_taking.validation_invalid_answer";
  }

  if (detail.includes("concurrent updates") || detail.includes("please retry")) {
    return "exam_taking.validation_retry";
  }

  // Any other error with a translated code (a 409 "conflict" race, "results_hidden", ...).
  const code = getApiErrorCode(error);
  if (code && i18n.exists(`errors.${code}`)) {
    return `errors.${code}`;
  }

  return fallbackKey;
};

export const resolveExamErrorText = (
  t,
  error,
  fallbackKey = "exam_taking.validation_unexpected_error"
) => {
  const key = resolveExamErrorKey(error, fallbackKey);
  return key ? t(key) : null;
};

const STATUS_KEY_MAP = {
  IN_PROGRESS: "exam_taking.status_in_progress",
  SUBMITTED: "exam_taking.status_submitted",
  GRADED: "exam_taking.status_graded",
  TIMED_OUT: "exam_taking.status_timed_out",
  CANCELLED: "exam_taking.status_cancelled",
  GRADING_FAILED: "exam_taking.status_grading_failed",
};

export const getAttemptStatusText = (t, status) => {
  const key = STATUS_KEY_MAP[status];
  return key ? t(key) : status;
};
